import { Sparkles, User } from "lucide-react";
import type { AssistantTurn } from "./useAssistant";
import { Markdown } from "./markdown";
import { AssistantSources } from "./AssistantSources";
import { AssistantFeedback } from "./AssistantFeedback";
import {
  DirectorNote,
  NoCoverageState,
  PiiWarningState,
  RateLimitState,
  ThinkingSkeleton,
  UnavailableState,
} from "./AssistantStates";

/**
 * Сесійний тред Q/A. SOURCES-GATE (AC-AI-SOURCES-GATE, hard): step-by-step /
 * процедура / policy-твердження рендеряться ВИКЛЮЧНО коли state=ok ТА
 * sources непорожні; інакше — відповідний стан (no-coverage тощо), 0 кроків.
 * Director-RO нота показується раз зверху, коли accessMode=readOnly.
 */
export function AssistantThread({
  thread,
  directorMode,
  onRetry,
  onAskAnother,
}: {
  thread: AssistantTurn[];
  directorMode: boolean;
  onRetry: (question: string) => void;
  onAskAnother: () => void;
}) {
  if (thread.length === 0) return null;

  return (
    <div className="space-y-5">
      {directorMode && <DirectorNote />}
      {thread.map((turn) => (
        <div key={turn.id} className="space-y-3">
          {/* питання */}
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground text-background" aria-hidden>
              <User className="h-3.5 w-3.5" />
            </span>
            <div className="pt-0.5 text-[13px] font-semibold text-foreground">{turn.question}</div>
          </div>

          {/* відповідь */}
          <div className="flex items-start gap-2.5" aria-live="polite">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-dark" aria-hidden>
              <Sparkles className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              {turn.loading ? (
                <ThinkingSkeleton />
              ) : (
                <Answer turn={turn} onRetry={onRetry} onAskAnother={onAskAnother} />
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Answer({
  turn,
  onRetry,
  onAskAnother,
}: {
  turn: AssistantTurn;
  onRetry: (q: string) => void;
  onAskAnother: () => void;
}) {
  const r = turn.result;
  if (!r) return null;

  // sources-gate: реальна процедура лише при ok + непорожніх sources.
  if (r.state === "ok" && r.sources.length > 0) {
    return (
      <>
        <Markdown text={r.answer} />
        <AssistantSources sources={r.sources} indexVersion={r.indexVersion} indexedAt={r.indexedAt} />
        <AssistantFeedback requestId={r.requestId} />
      </>
    );
  }

  switch (r.state) {
    case "pii_warning":
      return <PiiWarningState uiHint={r.uiHint} onRephrase={onAskAnother} />;
    case "rate_limited":
      return <RateLimitState />;
    case "unavailable":
      return <UnavailableState onRetry={() => onRetry(turn.question)} />;
    case "no_coverage":
    case "ok": // ok без sources → трактуємо як no-coverage (подвійний gate)
    default:
      return (
        <>
          <NoCoverageState onAskAnother={onAskAnother} />
          <AssistantFeedback requestId={r.requestId} />
        </>
      );
  }
}
